import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, AlertCircle, ArrowRight, Loader, CreditCard, ShieldCheck, Coins, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PaykuService } from '../services/paykuService';
import { toast } from 'react-hot-toast';

const PaymentConfirmation: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Verificando el estado de tu pago...');
  const [countdown, setCountdown] = useState<number>(5);
  const [packageInfo, setPackageInfo] = useState<any>(null);
  const [transactionId, setTransactionId] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<number>(2);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkPayment = async () => {
      try {
        // Extraer el ID de transacción de los parámetros de la URL
        const params = new URLSearchParams(location.search);
        const txid = params.get('txid');
        
        if (!txid) {
          setStatus('error');
          setMessage('No se encontró el ID de transacción. Por favor, verifica tu pago manualmente.');
          return;
        }
        
        setTransactionId(txid);
        
        // Intentar obtener información del paquete de sessionStorage
        const pendingTransactionStr = sessionStorage.getItem('pendingTransaction');
        if (pendingTransactionStr) {
          try {
            const pendingTransaction = JSON.parse(pendingTransactionStr);
            if (pendingTransaction.package_id) {
              // Obtener información del paquete
              const paykuService = PaykuService.getInstance();
              const pkg = paykuService.getPackageById(pendingTransaction.package_id);
              if (pkg) {
                setPackageInfo({
                  ...pkg,
                  amount: pendingTransaction.amount
                });
              }
            }
          } catch (error) {
            console.error('Error al parsear la información de la transacción pendiente:', error);
          }
        }
        
        // Obtener el usuario actual
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user?.id;
        
        if (!userId) {
          setStatus('error');
          setMessage('No se pudo identificar al usuario. Por favor, inicia sesión e intenta verificar tu pago manualmente.');
          return;
        }
        
        // Verificar la transacción con Payku
        const paykuService = PaykuService.getInstance();
        const result = await paykuService.verifyTransaction(txid, userId);
        
        if (result.success) {
          setStatus('success');
          setMessage(result.message);
          setCurrentStep(3);
          startRedirectCountdown();
        } else {
          setStatus('error');
          setMessage(result.message);
        }
      } catch (error) {
        console.error('Error al verificar el pago:', error);
        setStatus('error');
        setMessage('Error al verificar el pago. Por favor, intenta verificar manualmente.');
      }
    };
    
    checkPayment();
  }, [location.search]);

  const startRedirectCountdown = () => {
    setCountdown(5);
    
    const interval = setInterval(() => {
      setCountdown(prevCountdown => {
        if (prevCountdown <= 1) {
          clearInterval(interval);
          // Redirigir al dashboard
          navigate('/dashboard');
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  };

  // Renderizar los pasos del proceso
  const renderSteps = () => {
    const steps = [
      { id: 1, name: 'Pago', icon: <CreditCard className="h-5 w-5" /> },
      { id: 2, name: 'Verificación', icon: <ShieldCheck className="h-5 w-5" /> },
      { id: 3, name: 'Confirmación', icon: <Coins className="h-5 w-5" /> }
    ];

    return (
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className={`flex flex-col items-center ${currentStep >= step.id ? 'text-blue-500' : 'text-gray-500'}`}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${currentStep >= step.id ? 'border-blue-500 bg-blue-100' : 'border-gray-300'} mb-2`}>
                  {currentStep > step.id ? (
                    <CheckCircle2 className="h-6 w-6" />
                  ) : currentStep === step.id && status === 'loading' ? (
                    <div className="animate-spin">
                      <Loader className="h-5 w-5" />
                    </div>
                  ) : (
                    step.icon
                  )}
                </div>
                <span className={`text-sm font-medium ${currentStep >= step.id ? 'text-blue-500' : 'text-gray-500'}`}>
                  {step.name}
                </span>
              </div>
              
              {index < steps.length - 1 && (
                <div className={`w-12 h-1 mx-2 ${currentStep > index + 1 ? 'bg-blue-500' : 'bg-gray-300'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Renderizar información del paquete
  const renderPackageInfo = () => {
    if (!packageInfo) return null;
    
    return (
      <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
        <h3 className="text-lg font-medium text-blue-800 mb-2">Detalles de la compra</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Paquete</p>
            <p className="font-medium">{packageInfo.name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Monedas</p>
            <p className="font-medium">{packageInfo.vcoins}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Monto</p>
            <p className="font-medium">${packageInfo.amount.toLocaleString('es-CL')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">ID de Transacción</p>
            <p className="font-mono text-xs truncate">{transactionId}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">Confirmación de Pago</h1>
            
            {/* Pasos del proceso */}
            {renderSteps()}
            
            {/* Información del paquete */}
            {renderPackageInfo()}
            
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              {status === 'loading' && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                  <p className="text-lg text-gray-700 text-center">{message}</p>
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Esto puede tomar unos segundos...
                  </p>
                </div>
              )}
              
              {status === 'success' && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="bg-green-100 rounded-full p-4 mb-4">
                    <CheckCircle className="h-16 w-16 text-green-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-green-700 mb-2 text-center">¡Pago Exitoso!</h2>
                  <p className="text-lg text-gray-700 text-center mb-4">{message}</p>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200 mb-4 w-full max-w-md">
                    <p className="text-center text-green-700">
                      Serás redirigido al dashboard en <span className="font-bold">{countdown}</span> segundos...
                    </p>
                  </div>
                  <Link 
                    to="/dashboard" 
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
                  >
                    <span>Ir al Dashboard ahora</span>
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              )}
              
              {status === 'error' && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="bg-red-100 rounded-full p-4 mb-4">
                    <AlertCircle className="h-16 w-16 text-red-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-red-700 mb-2 text-center">Hubo un problema</h2>
                  <p className="text-lg text-gray-700 text-center mb-4">{message}</p>
                  <div className="flex flex-col space-y-4 w-full max-w-md">
                    <Link 
                      to="/verify-payment" 
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
                    >
                      <span>Verificar mi pago manualmente</span>
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                    <Link 
                      to="/coins" 
                      className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
                    >
                      <span>Volver a Comprar Monedas</span>
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-center mt-8">
              <Link 
                to="/dashboard" 
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
              >
                <span>Volver al Dashboard</span>
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation; 